import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import VerificationStepIndicator from '../components/VerificationStepIndicator';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

const steps = ['applicant', 'options', 'send'];

const methodOptions = [
  {
    value: 'kyc',
    label: 'KYC verification',
    description: 'Full customer verification workflow.',
  },
  {
    value: 'idv',
    label: 'IDV verification',
    description: 'Basic identity document verification.',
  },
];

const methodLabel = (method) => method === 'idv' ? 'IDV verification' : 'KYC verification';

const defaultSubject = (organizationName, method = 'kyc') => `${organizationName || 'idemGO'} invited you to complete a ${methodLabel(method)}`;

const defaultMessage = (firstName, method = 'kyc') => method === 'idv'
  ? `Hi${firstName ? ` ${firstName}` : ''},

Please complete your IDV verification using the secure link below.

You'll need your identity document.`
  : `Hi${firstName ? ` ${firstName}` : ''},

Please complete your KYC verification using the secure link below.

You'll need your identity document and a quick selfie.`;

function ageLabel(value) {
  if (!value) return 'Off';
  return `${value}+`;
}

export default function VerificationNew() {
  const navigate = useNavigate();
  const [step, setStep] = useState('applicant');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [reference, setReference] = useState('');
  const [verificationMethod, setVerificationMethod] = useState('kyc');
  const [minAge, setMinAge] = useState(0);
  const [deviceIpAnalysisEnabled, setDeviceIpAnalysisEnabled] = useState(false);
  const [organizationName, setOrganizationName] = useState('idemGO');
  const [inviteMethod, setInviteMethod] = useState('email');
  const [subject, setSubject] = useState(defaultSubject('idemGO', 'kyc'));
  const [message, setMessage] = useState(defaultMessage('', 'kyc'));
  const lastAutoSubject = useRef(defaultSubject('idemGO', 'kyc'));
  const lastAutoMessage = useRef(defaultMessage('', 'kyc'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [workspaceLocked, setWorkspaceLocked] = useState(false);
  const [attemptedApplicantContinue, setAttemptedApplicantContinue] = useState(false);
  const [touched, setTouched] = useState({ firstName: false, lastName: false, email: false });

  useEffect(() => {
    api.get('/organization')
      .then(({ data }) => {
        setMinAge(data.organization?.min_age || 0);
        setOrganizationName(data.organization?.name || 'idemGO');
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const nextSubject = defaultSubject(organizationName, verificationMethod);
    setSubject((current) => {
      if (!current || current === lastAutoSubject.current) {
        lastAutoSubject.current = nextSubject;
        return nextSubject;
      }
      return current;
    });

    const nextMessage = defaultMessage(firstName, verificationMethod);
    setMessage((current) => {
      if (!current || current === lastAutoMessage.current) {
        lastAutoMessage.current = nextMessage;
        return nextMessage;
      }
      return current;
    });
  }, [firstName, organizationName, verificationMethod]);

  const emailMode = inviteMethod === 'email';
  const applicantValid = !emailMode || (firstName.trim() && lastName.trim() && email.trim());
  const completed = useMemo(() => {
    const index = steps.indexOf(step);
    return steps.slice(0, Math.max(index, 0));
  }, [step]);

  const go = (nextStep) => {
    setError(null);
    setStep(nextStep);
  };

  const submit = async () => {
    if (emailMode && !applicantValid) return;
    setLoading(true);
    setError(null);
    setWorkspaceLocked(false);
    try {
      const { data } = await api.post('/verification-sessions', {
        invite_method: inviteMethod,
        claimed_first_name: firstName || null,
        claimed_last_name: lastName || null,
        applicant_email: email || null,
        applicant_reference: reference || null,
        verification_method: verificationMethod,
        device_ip_analysis_enabled: deviceIpAnalysisEnabled,
        min_age: Number(minAge),
        email_subject: subject,
        email_message: message,
      });
      const params = new URLSearchParams();
      params.set('created', inviteMethod === 'email' && !data.email_sent ? 'email_failed' : inviteMethod);
      if (inviteMethod === 'email' && email) params.set('email', email);
      navigate(`/dashboard/verifications/${data.id}?${params.toString()}`);
    } catch (e) {
      const code = e?.response?.data?.code || e?.response?.data?.error;
      setWorkspaceLocked(code === 'workspace_verification_required');
      setError(code === 'workspace_verification_required'
        ? 'Verify your identity to activate this workspace and unlock the free tier.'
        : code === 'usage_limit_exhausted'
        ? 'No verification usage available. Revoke an unused link or wait until next period.'
        : code === 'missing_invite_fields'
          ? 'First name, last name and email are required for email invitations.'
        : code === 'invalid_min_age'
            ? 'Choose a valid age check option.'
            : code === 'invalid_verification_method'
              ? 'Choose a valid verification method.'
              : `Could not create the ${methodLabel(verificationMethod)}.`);
    } finally {
      setLoading(false);
    }
  };

  const showFirstNameError = emailMode && !firstName.trim() && (attemptedApplicantContinue || touched.firstName);
  const showLastNameError = emailMode && !lastName.trim() && (attemptedApplicantContinue || touched.lastName);
  const showEmailError = emailMode && !email.trim() && (attemptedApplicantContinue || touched.email);

  return (
    <div data-testid="verification-new-page">
      <VerificationBreadcrumb current="New verification"/>

      <div className="mx-auto max-w-3xl space-y-6">
        <div className="text-center">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Verification setup</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-2">New verification</h1>
          <p className="mx-auto mt-2 max-w-xl text-sm text-zinc-500">Prepare the applicant, checks, and delivery before creating the link.</p>
        </div>
        <div className="mx-auto max-w-2xl">
          <VerificationStepIndicator current={step} completed={completed}/>
        </div>

        <section className="mx-auto max-w-2xl border border-zinc-200 rounded-sm p-6">
        {step === 'applicant' && (
          <>
            <h2 className="font-display text-lg font-semibold">Applicant</h2>
            <p className="text-sm text-zinc-500 mt-1">Email invitations require name and email. Manual links can be created without applicant details.</p>
            <div className="grid sm:grid-cols-2 gap-3 mt-5">
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">First name {emailMode ? '' : '(optional)'}</span>
                <input value={firstName} onBlur={() => setTouched((current) => ({ ...current, firstName: true }))} onChange={(e) => setFirstName(e.target.value)}
                  className={`w-full border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black ${showFirstNameError ? 'border-red-300' : 'border-zinc-200'}`}/>
                {showFirstNameError && <span className="mt-1 block text-xs text-red-700">Required for email invitation.</span>}
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Last name {emailMode ? '' : '(optional)'}</span>
                <input value={lastName} onBlur={() => setTouched((current) => ({ ...current, lastName: true }))} onChange={(e) => setLastName(e.target.value)}
                  className={`w-full border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black ${showLastNameError ? 'border-red-300' : 'border-zinc-200'}`}/>
                {showLastNameError && <span className="mt-1 block text-xs text-red-700">Required for email invitation.</span>}
              </label>
            </div>
            <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-4 mb-1">Email {emailMode ? '' : '(optional)'}</label>
            <input value={email} onBlur={() => setTouched((current) => ({ ...current, email: true }))} onChange={(e) => setEmail(e.target.value)} type="email"
              className={`w-full border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black ${showEmailError ? 'border-red-300' : 'border-zinc-200'}`}/>
            {emailMode && attemptedApplicantContinue && !applicantValid && (
              <p className="text-xs text-red-700 mt-2">First name, last name and email are required for email invitations.</p>
            )}
            <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-4 mb-1">Reference (optional)</label>
            <input value={reference} onChange={(e) => setReference(e.target.value)}
              className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black" placeholder="user_42"/>
          </>
        )}

        {step === 'options' && (
          <>
            <h2 className="font-display text-lg font-semibold">Options</h2>
            <p className="text-sm text-zinc-500 mt-1">Configure checks for this verification.</p>
            <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-5 mb-1">Verification method</label>
            <div className="grid sm:grid-cols-2 gap-2">
              {methodOptions.map((option) => (
                <button key={option.value} type="button" onClick={() => setVerificationMethod(option.value)}
                  className={`border rounded-sm px-3 py-3 text-left ${verificationMethod === option.value ? 'border-black bg-zinc-900 text-white' : 'border-zinc-200 text-zinc-700'}`}>
                  <span className="block text-sm font-medium">{option.label}</span>
                  <span className={`block text-xs mt-1 ${verificationMethod === option.value ? 'text-zinc-300' : 'text-zinc-500'}`}>{option.description}</span>
                </button>
              ))}
            </div>
            <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-5 mb-1">Age check</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[0, 16, 18, 21].map((value) => (
                <button key={value} type="button" onClick={() => setMinAge(value)}
                  className={`border rounded-sm px-3 py-3 text-sm text-left ${Number(minAge) === value ? 'border-black bg-zinc-900 text-white' : 'border-zinc-200 text-zinc-700'}`}>
                  {ageLabel(value)}
                </button>
              ))}
            </div>
            <div className="mt-5">
              <button type="button" onClick={() => setDeviceIpAnalysisEnabled((current) => !current)}
                className={`w-full border rounded-sm px-3 py-3 text-left ${deviceIpAnalysisEnabled ? 'border-black bg-zinc-900 text-white' : 'border-zinc-200 text-zinc-700'}`}>
                <span className="flex items-center justify-between gap-3">
                  <span>
                    <span className="block text-sm font-medium">Device and IP analysis</span>
                    <span className={`block text-xs mt-1 ${deviceIpAnalysisEnabled ? 'text-zinc-300' : 'text-zinc-500'}`}>Capture device, browser, IP and session risk signals</span>
                  </span>
                  <span className={`shrink-0 text-[10px] uppercase tracking-widest font-mono ${deviceIpAnalysisEnabled ? 'text-zinc-300' : 'text-zinc-500'}`}>
                    {deviceIpAnalysisEnabled ? 'On' : 'Off'}
                  </span>
                </span>
              </button>
            </div>
            <div className="mt-5 space-y-2">
              {[
                ['Proof of address', 'Verify address via utility bills or bank statements'],
                ['Database check', 'Cross-check identity against trusted databases'],
                ['AML screening', 'Sanctions, PEP, and adverse media checks'],
              ].map(([title, body]) => (
                <div key={title} className="border border-zinc-200 rounded-sm p-3 opacity-60">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">{title}</p>
                      <p className="text-xs text-zinc-500 mt-1">{body}</p>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">Coming soon</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-3">Additional checks may increase usage in future plans.</p>
          </>
        )}

        {step === 'send' && (
          <>
            <h2 className="font-display text-lg font-semibold">Send</h2>
            <p className="text-sm text-zinc-500 mt-1">Choose delivery, review the setup, then create the {methodLabel(verificationMethod)} link.</p>
            <div className="mt-5">
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Delivery method</label>
              <div className="grid sm:grid-cols-2 gap-2">
                <button type="button" onClick={() => setInviteMethod('email')}
                  className={`border rounded-sm px-3 py-3 text-left ${inviteMethod === 'email' ? 'border-black bg-zinc-900 text-white' : 'border-zinc-200 text-zinc-700'}`}>
                  <span className="block text-sm font-medium">Send email invitation</span>
                  <span className={`block text-xs mt-1 ${inviteMethod === 'email' ? 'text-zinc-300' : 'text-zinc-500'}`}>Review before sending</span>
                </button>
                <button type="button" onClick={() => setInviteMethod('link')}
                  className={`border rounded-sm px-3 py-3 text-left ${inviteMethod === 'link' ? 'border-black bg-zinc-900 text-white' : 'border-zinc-200 text-zinc-700'}`}>
                  <span className="block text-sm font-medium">Generate link manually</span>
                  <span className={`block text-xs mt-1 ${inviteMethod === 'link' ? 'text-zinc-300' : 'text-zinc-500'}`}>No email sent</span>
                </button>
              </div>
              {emailMode && (
                <>
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-5 mb-1">Email subject</label>
                  <input value={subject} onChange={(e) => setSubject(e.target.value)}
                    className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-4 mb-1">Email message</label>
                  <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={7}
                    className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>
                  <p className="text-xs text-zinc-500 mt-2 font-mono">The secure {methodLabel(verificationMethod)} link is added to the email automatically.</p>
                </>
              )}
            </div>
            <div className="mt-6 border-t border-zinc-200 pt-5">
              <h3 className="font-display text-lg font-semibold">Review</h3>
              <div className="mt-3 divide-y divide-zinc-100 text-sm">
                <div className="grid grid-cols-3 gap-3 py-3"><span className="text-zinc-500">Applicant</span><span className="col-span-2">{[firstName, lastName].filter(Boolean).join(' ') || 'Not provided'}</span></div>
                <div className="grid grid-cols-3 gap-3 py-3"><span className="text-zinc-500">Email</span><span className="col-span-2 font-mono text-xs">{email || 'Not provided'}</span></div>
                <div className="grid grid-cols-3 gap-3 py-3"><span className="text-zinc-500">Reference</span><span className="col-span-2 font-mono text-xs">{reference || 'Not provided'}</span></div>
                <div className="grid grid-cols-3 gap-3 py-3"><span className="text-zinc-500">Method</span><span className="col-span-2">{methodLabel(verificationMethod)}</span></div>
                <div className="grid grid-cols-3 gap-3 py-3"><span className="text-zinc-500">Age check</span><span className="col-span-2">{ageLabel(Number(minAge))}</span></div>
                <div className="grid grid-cols-3 gap-3 py-3"><span className="text-zinc-500">Device and IP analysis</span><span className="col-span-2">{deviceIpAnalysisEnabled ? 'On' : 'Off'}</span></div>
                <div className="grid grid-cols-3 gap-3 py-3"><span className="text-zinc-500">Delivery</span><span className="col-span-2">{emailMode ? 'Send email invitation' : 'Generate link manually'}</span></div>
              </div>
            </div>
          </>
        )}

        {error && (
          <p className="mt-4 text-sm text-red-700">
            {error}
            {workspaceLocked && (
              <> <Link to="/settings" className="font-medium underline">Go to Workspace verification</Link></>
            )}
          </p>
        )}

        <div className="mt-6 flex justify-between gap-2">
          <button type="button" onClick={() => go(steps[Math.max(steps.indexOf(step) - 1, 0)])} disabled={step === 'applicant' || loading}
            className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400 disabled:opacity-40">Back</button>
          {step === 'send' ? (
            <button type="button" onClick={submit} disabled={loading || !applicantValid}
              className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50">
              {loading ? 'Creating...' : emailMode ? 'Send invitation' : 'Create link'}
            </button>
          ) : (
            <button type="button" onClick={() => {
              if (step === 'applicant' && !applicantValid) {
                setAttemptedApplicantContinue(true);
                return;
              }
              go(steps[steps.indexOf(step) + 1]);
            }} disabled={false}
              className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50">Continue</button>
          )}
        </div>
        </section>
      </div>
    </div>
  );
}
